import { useCallback, useEffect, useRef, useState } from 'react';
import type { AuthUser } from '@/models/auth';
import { ALLOWED_EMAILS, AUTH_DISABLED } from '@/services/config';
import { authService } from '@/services/authService';
import { clearStored, loadStored, saveTokens } from '@/services/authStorage';

const STUB_USER: AuthUser = {
  id: 'local',
  email: 'local@issues',
  first_name: 'Local',
  last_name: 'User',
  is_superadmin: true,
};

export function useAuth() {
  const [user, setUser] = useState<AuthUser | null>(AUTH_DISABLED ? STUB_USER : null);
  const [loading, setLoading] = useState(!AUTH_DISABLED);
  const [error, setError] = useState('');
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleRefresh = useCallback((expiresAt: number) => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    const delay = Math.max(0, expiresAt - Date.now() - 60_000);
    refreshTimer.current = setTimeout(async () => {
      const stored = loadStored();
      if (!stored) return;
      try {
        const data = await authService.refresh(stored.refresh_token);
        saveTokens(data.access_token, data.refresh_token, data.expires_in);
        scheduleRefresh(Date.now() + data.expires_in * 1000);
      } catch {
        clearStored();
        setUser(null);
        setError('Session abgelaufen — bitte erneut anmelden.');
      }
    }, delay);
  }, []);

  const fetchMe = useCallback(async (accessToken: string): Promise<AuthUser | null> => {
    const u = await authService.me(accessToken);
    if (!u) return null;
    if (!ALLOWED_EMAILS.includes(u.email)) {
      clearStored();
      return null;
    }
    return u;
  }, []);

  useEffect(() => {
    if (AUTH_DISABLED) return;

    // Handle OAuth callback
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    if (window.location.pathname === '/auth/google/callback' && code) {
      setLoading(true);
      const redirectUri = `${window.location.origin}/auth/google/callback`;
      authService
        .exchangeCode(code, redirectUri)
        .then((data) => {
          if (data.user && !ALLOWED_EMAILS.includes(data.user.email)) {
            setError('Zugriff verweigert. Kontaktiere den Administrator.');
            return;
          }
          saveTokens(data.access_token, data.refresh_token, data.expires_in);
          setUser(data.user);
          scheduleRefresh(Date.now() + data.expires_in * 1000);
        })
        .catch(() => setError('Google Login fehlgeschlagen'))
        .finally(() => {
          window.history.replaceState({}, '', '/');
          setLoading(false);
        });
      return;
    }

    const stored = loadStored();
    if (!stored) {
      setLoading(false);
      return;
    }

    if (stored.expires_at > Date.now()) {
      fetchMe(stored.access_token).then((u) => {
        if (u) {
          setUser(u);
          scheduleRefresh(stored.expires_at);
        } else {
          clearStored();
          setError('Session abgelaufen — bitte erneut anmelden.');
        }
        setLoading(false);
      });
    } else {
      authService
        .refresh(stored.refresh_token)
        .then(async (data) => {
          saveTokens(data.access_token, data.refresh_token, data.expires_in);
          const u = await fetchMe(data.access_token);
          if (u) {
            setUser(u);
            scheduleRefresh(Date.now() + data.expires_in * 1000);
          } else {
            clearStored();
            setError('Session abgelaufen — bitte erneut anmelden.');
          }
        })
        .catch(() => {
          clearStored();
          setError('Session abgelaufen — bitte erneut anmelden.');
        })
        .finally(() => setLoading(false));
    }

    return () => {
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
    };
  }, [fetchMe, scheduleRefresh]);

  const startGoogleLogin = useCallback(async () => {
    try {
      const redirectUri = `${window.location.origin}/auth/google/callback`;
      const url = await authService.authorizeUrl(redirectUri);
      window.location.href = url;
    } catch {
      setError('Verbindung fehlgeschlagen');
    }
  }, []);

  const logout = useCallback(() => {
    if (AUTH_DISABLED) return;
    const stored = loadStored();
    if (stored) authService.logout(stored.refresh_token);
    clearStored();
    setUser(null);
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
  }, []);

  return { user, loading, error, logout, startGoogleLogin };
}
